import { useState } from "react";
import { useBackNav, goBack } from "../lib/backnav.ts";
import { useQuery, useQueryClient } from "@tanstack/react-query";

const TEXT = "#cfe0ff", MUTED = "#8a93a6", GREEN = "#6fcf97", RED = "#e07070",
  AMBER = "#e0a458", ACCENT = "#6699ff", BORDER = "rgba(255,255,255,0.08)",
  CARD = "rgba(255,255,255,0.03)";
const card = { background: CARD, border: `1px solid ${BORDER}`, borderRadius: 8,
  padding: "14px 16px", marginBottom: 14 } as const;
const lbl = { fontSize: 11, color: MUTED, letterSpacing: 0.6, marginBottom: 10,
  textTransform: "uppercase" } as const;
const btn = { fontSize: 12, padding: "5px 12px", borderRadius: 5, cursor: "pointer",
  border: "1px solid rgba(255,255,255,0.14)", background: "transparent", color: TEXT } as const;
const backBtn = { ...btn, fontWeight: 700, color: "#cfe0ff",
  border: "1px solid #6699ff88", background: "rgba(102,153,255,0.18)",
  padding: "7px 16px" } as const;
const sevColor = (s: string) =>
  s === "critical" ? RED : s === "warning" ? AMBER : MUTED;

async function j<T>(p: string, init?: RequestInit): Promise<T> {
  const r = await fetch(p, { headers: { Accept: "application/json",
    "Content-Type": "application/json" }, ...init });
  const b = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error((b as any).detail ?? `Failed (${r.status})`);
  return b as T;
}

export function ResultsTab({ initialRfp }: { initialRfp: string | null }) {
  const [rfp, setRfp] = useState<string | null>(initialRfp);
  useBackNav([{ active: rfp != null, close: () => setRfp(null) }]);
  if (rfp) return <ResultDetail rfp={rfp} back={goBack} />;
  return <Queue open={setRfp} />;
}

function Queue({ open }: { open: (r: string) => void }) {
  const list = useQuery({ queryKey: ["hitl", "rfps"], queryFn: () => j<any>("/api/v1/hitl/rfps") });
  return (
    <div className="panel">
      <div className="panel-head"><h2 style={{ margin: 0 }}>Scoring results</h2></div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {(list.data?.rfps ?? []).map((r: any) => (
          <Row key={r.rfp_id} rfp={r.rfp_id} count={r.proposal_count} open={() => open(r.rfp_id)} />
        ))}
      </div>
    </div>
  );
}

function Row({ rfp, count, open }: { rfp: string; count: number; open: () => void }) {
  const sc = useQuery({ queryKey: ["hitl", "scoring", rfp],
    queryFn: () => j<any>(`/api/v1/hitl/rfp/${encodeURIComponent(rfp)}/scoring`) });
  const d = sc.data;
  const sub = !d ? `${count} proposal(s)` :
    d.scored ? `SCORED · ${d.scored_at ?? ""}` :
    d.ready ? "READY · not yet scored" : "NOT READY · finalize proposals first";
  return (
    <div style={{ ...card, marginBottom: 0, cursor: "pointer" }} onClick={open}>
      <span style={{ color: ACCENT, fontWeight: 600, fontSize: 14 }}>{rfp}</span>
      <span style={{ color: MUTED, fontSize: 12, marginLeft: 14 }}>{sub}</span>
    </div>
  );
}

const MEMO_ORDER = ["executive_summary", "recommendation", "hard_fail_summary",
  "flags_summary", "stability_assessment"];
const title = (k: string) => k.replaceAll("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());

function ResultDetail({ rfp, back }: { rfp: string; back: () => void }) {
  const qc = useQueryClient();
  const sc = useQuery({ queryKey: ["hitl", "scoring", rfp],
    queryFn: () => j<any>(`/api/v1/hitl/rfp/${encodeURIComponent(rfp)}/scoring`) });
  const who = useQuery({ queryKey: ["hitl", "whoami"], queryFn: () => j<any>("/api/v1/hitl/whoami") });
  const [msg, setMsg] = useState<string | null>(null);
  const d = sc.data;
  const isOperator = who.data?.role === "operator";
  if (!d) return <div style={{ color: MUTED, padding: 20 }}>Loading…</div>;
  if (!d.scored) return (
    <div className="panel">
      <button style={backBtn} onClick={back}>← Results</button>
      <div style={{ ...card, marginTop: 12, color: MUTED, fontSize: 13 }}>
        Not scored yet — run scoring from the Review tab.</div>
    </div>
  );

  const eo = d.evaluation?.evaluation_output ?? d.evaluation ?? {};
  const ranked: any[] = eo.ranked_list ?? [];
  const breakdowns: any[] = eo.score_breakdowns ?? [];
  const hfr = eo.hard_fail_report ?? {};
  const amb = eo.ambiguity_report ?? {};
  const stab = eo.rank_stability ?? {};
  const memo = (eo.board_memo ?? {}).sections ?? {};
  const nameOf: Record<string, string> = {};
  ranked.forEach((e) => { if (e.proposal_id) nameOf[e.proposal_id] = e.entity_name ?? e.proposal_id; });
  const clean = (s: any) => {
    let out = String(s ?? "");
    Object.entries(nameOf).forEach(([pid, nm]) => { out = out.split(pid).join(nm); });
    return out.split(rfp).join("this RFP");
  };
  const fmt = (v: any, dp = 2) => (v == null || isNaN(Number(v)) ? "—" : Number(v).toFixed(dp));
  const failReasons: Record<string, string[]> = {};
  const failDims: Record<string, string[]> = {};
  (hfr.failed_proposals ?? []).forEach((f: any) => {
    failReasons[f.proposal_id] = (f.failed_rules ?? []).flatMap((r: any) =>
      (r.violations ?? []).map((v: any) => String(v.detail ?? "")));
    failDims[f.proposal_id] = (f.failed_rules ?? []).map((r: any) =>
      String(r.name ?? "").split("_")[0]);
  });
  const passing = ranked.filter((e) => !e.hard_fail);
  const fails = ranked.filter((e) => e.hard_fail);
  const stabLabel = typeof stab === "string" ? stab :
    stab.label ?? stab.classification ??
    (stab.stable === true ? "STABLE" : stab.stable === false ? "UNSTABLE" : "—");
  const memoKeys = [...MEMO_ORDER.filter((k) => memo[k]),
    ...Object.keys(memo).filter((k) => !MEMO_ORDER.includes(k))];
  const flagRows: any[] = (amb.proposals ?? []).flatMap((pr: any) =>
    [...(pr.auto_flags ?? []), ...(pr.manual_flags ?? [])].map((f: any) =>
      ({ ...f, entity: pr.entity_name ?? nameOf[pr.proposal_id] ?? pr.proposal_id })));

  const selectWinner = (e: any) => {
    if (!window.confirm(`Award this engagement to ${e.entity_name}?`)) return;
    j<any>(`/api/v1/hitl/rfp/${encodeURIComponent(rfp)}/select-winner`, {
      method: "POST", body: JSON.stringify({ proposal_id: e.proposal_id,
        entity_name: e.entity_name }) })
      .then(() => { setMsg(`Winner recorded: ${e.entity_name}`);
        qc.invalidateQueries({ queryKey: ["hitl"] }); })
      .catch((er: Error) => setMsg(er.message));
  };

  const th = { textAlign: "left" as const, padding: "5px 10px", fontSize: 10,
    color: MUTED, textTransform: "uppercase" as const, borderBottom: `1px solid ${BORDER}` };
  const td = { padding: "6px 10px", fontSize: 12, color: TEXT,
    borderBottom: `1px solid ${BORDER}` };

  return (
    <div className="panel">
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
        <button style={backBtn} onClick={back}>← Results</button>
        <h2 style={{ margin: 0, flex: 1 }}>Evaluation Results</h2>
        <span style={{ fontSize: 12, color: MUTED }}>{d.scored_at}</span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: 12, marginBottom: 14 }}>
        {[["Proposals scored", String(ranked.length), "approved submissions"],
          ["Passing", String(passing.length), "met all hard gates"],
          ["Hard fails", String(fails.length), "disqualified"],
          ["Rank stability", String(stabLabel), "±10% perturbation"]].map(([k, v, s]) => (
          <div key={k} style={{ ...card, marginBottom: 0 }}>
            <div style={lbl}>{k}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: TEXT }}>{v}</div>
            <div style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>{s}</div>
          </div>
        ))}
      </div>

      {memoKeys.length > 0 && (
        <div style={card}>
          <div style={lbl}>Board memo</div>
          {memoKeys.map((k) => (
            <div key={k} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: ACCENT, marginBottom: 4 }}>
                {title(k)}</div>
              <div style={{ fontSize: 13, color: TEXT, lineHeight: 1.6,
                borderLeft: `2px solid ${ACCENT}44`, paddingLeft: 12,
                whiteSpace: "pre-wrap" }}>{clean(memo[k])}</div>
            </div>
          ))}
        </div>
      )}

      <div style={card}>
        <div style={lbl}>Ranked proposals</div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>
            {["Rank", "Entity", "Score", "Band", "Most sensitive", ""].map((h) => (
              <th key={h} style={th}>{h}</th>))}
          </tr></thead>
          <tbody>
            {ranked.map((e, i) => (
              <tr key={i}>
                <td style={td}>{e.rank ?? (e.hard_fail ? "—" : i + 1)}</td>
                <td style={{ ...td, fontWeight: 700,
                  color: e.hard_fail ? RED : TEXT }}>{e.entity_name}
                  {e.hard_fail && " · HARD FAIL"}</td>
                <td style={{ ...td, color: ACCENT, fontWeight: 700 }}>
                  {e.hard_fail
                    ? `0.00 (hyp. ${fmt(e.hypothetical_score)})`
                    : fmt(e.final_score)}</td>
                <td style={{ ...td, color: MUTED }}>
                  {e.band_low != null ? `[${fmt(e.band_low)}–${fmt(e.band_high)}]` : "—"}</td>
                <td style={{ ...td, color: MUTED }}>
                  {e.most_sensitive_dimension ?? "—"}</td>
                <td style={{ ...td, textAlign: "right" }}>
                  {d.winner?.proposal_id === e.proposal_id && (
                    <span style={{ fontSize: 11, fontWeight: 700, color: GREEN,
                      border: `1px solid ${GREEN}55`, borderRadius: 4,
                      padding: "2px 8px" }}>WINNER · {d.winner.selected_by}</span>)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {msg && <div style={{ fontSize: 12, color: MUTED, marginTop: 8 }}>{msg}</div>}
      </div>

      <div style={card}>
        <div style={lbl}>Score breakdowns</div>
        {[...breakdowns].sort((a, b) =>
          (a.hard_fail ? -1 : Number(a.final_score ?? 0)) <
          (b.hard_fail ? -1 : Number(b.final_score ?? 0)) ? 1 : -1)
          .map((b, i) => (
          <div key={i} style={{ marginBottom: 18 }}>
            <div style={{ display: "inline-flex", gap: 8, alignItems: "center",
              fontSize: 13, fontWeight: 700, marginBottom: 4,
              padding: "3px 10px", borderRadius: 6,
              border: `1px solid ${b.hard_fail ? RED + "55" : i === 0 ? GREEN + "66" : "transparent"}`,
              background: i === 0 && !b.hard_fail ? GREEN + "14" : "transparent",
              color: b.hard_fail ? RED : TEXT }}>
              {b.entity_name}{b.hard_fail
                ? " — HARD FAIL" : ` — ${fmt(b.final_score)}`}
              {i === 0 && !b.hard_fail && (
                <span style={{ fontSize: 10, color: GREEN }}>
                  {d.winner?.proposal_id === b.proposal_id ? "WINNER" : "TOP RANKED"}</span>)}
            </div>
            {b.hard_fail && (failReasons[b.proposal_id] ?? []).map((r, ri) => (
              <div key={ri} style={{ fontSize: 12, color: RED, marginBottom: 4 }}>{clean(r)}</div>
            ))}
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                {["Dimension", "Raw", "Aligned", "Weight", "Strength"].map((h) => (
                  <th key={h} style={th}>{h}</th>))}
              </tr></thead>
              <tbody>
                {Object.entries(b.dimensions ?? {}).map(([dk, dv]: [string, any]) => {
                  const hot = b.hard_fail && (failDims[b.proposal_id] ?? [])
                    .some((t) => t && (dk.includes(t) || t.includes(dk)));
                  return (
                  <tr key={dk} style={hot ? { background: RED + "1a",
                    outline: `1px solid ${RED}44` } : undefined}>
                    <td style={td}>{dk.replaceAll("_", " ")}</td>
                    <td style={td}>{fmt(dv?.raw ?? dv?.raw_score, 1)}</td>
                    <td style={td}>{fmt(dv?.aligned ?? dv?.aligned_score, 1)}</td>
                    <td style={{ ...td, color: MUTED }}>{fmt(dv?.weight, 4)}</td>
                    <td style={{ ...td, color: MUTED }}>{fmt(dv?.strength ?? dv?.data_strength)}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>
              Completeness: {b.completeness_ratio != null
                ? `${(Number(b.completeness_ratio) * 100).toFixed(1)}%` : "—"}
              {b.completeness_multiplier != null &&
                ` (multiplier: ${fmt(b.completeness_multiplier, 3)})`}
            </div>
          </div>
        ))}
      </div>

      {flagRows.length > 0 && (
        <div style={card}>
          <div style={lbl}>Review flags ({amb.total_flags ?? flagRows.length}
            {amb.by_severity ? ` · critical ${amb.by_severity.critical} / warning ${amb.by_severity.warning} / info ${amb.by_severity.info}` : ""})</div>
          {(["critical", "warning", "info"] as const).map((sev) => {
            const rows = flagRows.filter((f) => (f.severity ?? "info") === sev);
            if (!rows.length) return null;
            return [...rows].sort((a, b) =>
              String(a.entity).localeCompare(String(b.entity))).map((f, i) => (
              <div key={sev + i} style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap",
                padding: "5px 0", fontSize: 12, borderBottom: `1px solid ${BORDER}` }}>
                <span style={{ color: sevColor(sev), fontWeight: 700, minWidth: 66 }}>
                  {sev.toUpperCase()}</span>
                <span style={{ color: ACCENT, fontWeight: 600, minWidth: 110 }}>
                  {f.entity}</span>
                <span style={{ color: TEXT, flex: 1 }}>
                  {clean(f.message).replace(/\s*\(flagged by [^)]*\)\s*$/, "")}</span>
                <span style={{ color: MUTED, fontSize: 11 }}>{f.flagged_by ?? f.flag_type}</span>
              </div>
            ));
          })}
        </div>
      )}

      <div style={card}>
        <div style={lbl}>Scoring run metadata</div>
        {[["Generated", d.scored_at],
          ["Rubric", eo.metadata?.rubric_version ?? d.artifact?.rubric_version],
          ["Proposals", ranked.map((e: any) => e.entity_name).join(", ")],
          ["Engagement status", d.engagement_status]].map(([k, v]) => (
          <div key={String(k)} style={{ display: "flex", gap: 12, fontSize: 12, padding: "3px 0" }}>
            <span style={{ color: MUTED, minWidth: 160 }}>{k}</span>
            <span style={{ color: TEXT }}>{String(v ?? "—")}</span>
          </div>
        ))}
      </div>

      <div style={card}>
        <div style={lbl}>Winner selection</div>
        {d.winner ? (
          <div style={{ fontSize: 14, color: GREEN, fontWeight: 700 }}>
            Winner: {d.winner.entity_name}
            <span style={{ fontSize: 12, color: MUTED, fontWeight: 400, marginLeft: 10 }}>
              selected by {d.winner.selected_by} at {d.winner.selected_at}</span>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 13, color: MUTED, marginBottom: 12 }}>
              Select the winning market maker to proceed with the engagement.
            </div>
            {passing.map((e, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
                border: `1px solid ${BORDER}`, borderRadius: 8, padding: "12px 16px",
                marginBottom: 10 }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: TEXT }}>
                  #{e.rank ?? i + 1} {e.entity_name}</span>
                <span style={{ fontSize: 13, color: MUTED, flex: 1 }}>
                  Score: {fmt(e.final_score)}</span>
                {isOperator ? (
                  <button style={{ ...btn, background: GREEN, color: "#0a0e17",
                    border: "none", fontWeight: 700, padding: "8px 18px" }}
                    onClick={() => selectWinner(e)}>Select Winner</button>
                ) : (
                  <span style={{ fontSize: 11, color: MUTED }}>operator only</span>
                )}
              </div>
            ))}
          </>
        )}
      </div>

    </div>
  );
}
